import { rem, useToasts } from "@certn/cascadia";
import {
  Container,
  ContractScroll,
  Grid,
  GridItem,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import { ActionBar } from "@certn/cascadia-react/components/action-bar/ActionBar";
import { useAPIRequest, usePeopleAPI } from "@certn/legacy-api";
import { css } from "@emotion/css";
import { useContext, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ConsentRequiredModal } from "./ConsentRequiredModal";
import constants from "../../../../../constants";
import { ApplicationContext } from "../../Application.context";
import { SectionProps } from "../types";

export function ConsentsSection({ section, onNext, onBack }: SectionProps) {
  const { t } = useTranslation();
  const { applicantId } = useParams();
  const { show } = useToasts();
  const request = useAPIRequest();
  const [signed, setSigned] = useState(false);
  const [contractsSigned, setContractsSigned] = useState<
    Record<string, boolean>
  >({});
  const [currentContractVersion, setCurrentContractVersion] =
    useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const { completeSection } = useContext(ApplicationContext);

  const url =
    `/internal/people/applicant/${applicantId}/consent-and-disclosure-templates/` as "/internal/people/applicant/{id}/consent-and-disclosure-templates/";
  const { data: consents } = usePeopleAPI<typeof url>(url);

  const [currentConsentIndex, setCurrentConsentIndex] = useState<number>(0);
  const currentConsent = consents?.[currentConsentIndex];
  const currentConsentID = currentConsent?.external_id ?? "";

  const contractIDs = useMemo(
    () => consents?.map(({ external_id }) => external_id),
    [consents]
  );
  const renderedData = useMemo(
    () =>
      consents?.reduce((i, consent) => ({ ...i, ...consent.render_data }), {}),
    [consents]
  );

  const handleNext = async () => {
    if (
      !signed &&
      consents?.length !== 0 &&
      !contractsSigned[currentConsentID]
    ) {
      setShowMessage(true);
      return;
    }

    if (!contractsSigned[currentConsentID]) {
      try {
        await request(
          `/internal/people/applicant/${applicantId}/acknowledge-document/`,
          {
            method: "POST",
            body: JSON.stringify({
              render_data: consents?.[currentConsentIndex].render_data,
              template_id: consents?.[currentConsentIndex].template_id,
              version_id: currentContractVersion,
            }),
          }
        );
      } catch (e) {
        console.error("Error signing the document", e);
        show({
          title: t("Something went wrong, try again"),
          type: "error",
        });
      }
    }

    if (consents?.[currentConsentIndex + 1]) {
      setSigned(false);
      setContractsSigned((prev) => ({
        ...prev,
        [currentConsentID]: true,
      }));
      setCurrentConsentIndex(currentConsentIndex + 1);
      return;
    }

    try {
      await request(`/internal/people/applicant/${applicantId}/update/`, {
        method: "POST",
        body: JSON.stringify({ consents_acknowledged: true }),
      });
      completeSection();
      onNext();
    } catch (e) {
      console.error("Error completing consent section:", e);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }
  };

  const loadPreviousConsent = () => {
    setCurrentConsentIndex((num) => num - 1);
  };

  return (
    <Container>
      <Grid aria-label={t("Consent")} testID="consent-section-container">
        <GridItem alignItems="left">
          <Text
            as="h1"
            style="form/page-title"
            className={css({
              marginTop: rem("space-6"),
            })}
          >
            {currentConsent?.template_name}
          </Text>
          <Text
            style="form/body-primary"
            color="text-weak"
            className={css({ marginTop: rem("space-2") })}
          >
            <Trans>
              We need to obtain your consent before we can perform the requested
              background check services.
            </Trans>
          </Text>
        </GridItem>

        <GridItem>
          <SectionMessage
            icon="verified_user"
            sectionTitle={t("Your privacy is important to us")}
            type="success"
          >
            <Trans>
              Certn will only collect information that is needed to complete
              your check.
            </Trans>
          </SectionMessage>
        </GridItem>

        <GridItem>
          {currentConsent && applicantId && (
            <ContractScroll
              accessID={constants.VITE_CLICKWRAP_ACCESS_ID}
              signerID={applicantId}
              index={currentConsentIndex}
              contractIDs={contractIDs ?? []}
              isSigned={contractsSigned[currentConsentID] || signed}
              onChange={setSigned}
              onContractLoad={({ isSigned, versionID }) => {
                setContractsSigned((prev) => ({
                  ...prev,
                  [currentConsentID]: prev[currentConsentID] || isSigned,
                }));
                setCurrentContractVersion(versionID);
              }}
              renderData={renderedData}
              label={
                currentConsent.template_type === "CONSENT"
                  ? t("I understand and agree to this document")
                  : t(
                      "I acknowledge that I have received and understand this document"
                    )
              }
            />
          )}
        </GridItem>

        <GridItem>
          <ActionBar
            primaryText={t("Next step")}
            onPrimaryClick={async () => {
              setIsLoading(true);

              try {
                await handleNext();
              } finally {
                setIsLoading(false);
              }
            }}
            backText={t("Back")}
            onBackClick={currentConsentIndex > 0 ? loadPreviousConsent : onBack}
            isDisabled={isLoading}
            isLoading={isLoading}
          />
        </GridItem>

        <ConsentRequiredModal
          show={showMessage}
          onClose={() => setShowMessage(false)}
        />
      </Grid>
    </Container>
  );
}
